import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { apiFetch } from "@/api";
import { cn } from "@/ui";

// Live voice-to-voice with OpenAI Realtime (gpt-realtime-2.1) over WebRTC.
// The backend mints a short-lived ephemeral token (kid-safety instructions + voice baked in);
// the browser connects straight to OpenAI for the lowest possible latency and natural barge-in.
const LABELS = { connecting: "Connecting…", listening: "Listening…", thinking: "Thinking…", speaking: "MAXx is talking…", error: "Voice unavailable" };

export function VoiceSession({ open, tab, onClose }) {
  const [phase, setPhase] = useState("connecting");
  const [caption, setCaption] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const runningRef = useRef(false);
  const replyRef = useRef("");

  useEffect(() => {
    if (!open) return;
    runningRef.current = true;
    setPhase("connecting"); setCaption(""); setErrorMsg("");
    start();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    runningRef.current = false;
    try { dcRef.current?.close(); } catch {}
    try { pcRef.current?.getSenders().forEach((s) => s.track && s.track.stop()); } catch {}
    try { pcRef.current?.close(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    if (audioRef.current) { try { audioRef.current.srcObject = null; } catch {} }
    pcRef.current = null; dcRef.current = null; streamRef.current = null;
  };

  const start = async () => {
    try {
      // iOS/Safari: prime the audio element inside the user gesture so remote audio can play later.
      try { await audioRef.current?.play().catch(() => {}); } catch {}
      const sess = await apiFetch("/user/voice/realtime-session", { method: "POST" });
      if (!runningRef.current) return;
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => {
        if (!audioRef.current) return;
        audioRef.current.srcObject = e.streams[0];
        audioRef.current.play().catch(() => {});
      };
      // Echo cancellation keeps MAXx from hearing itself through the phone speaker (which made it
      // interrupt itself / reply to the wrong thing on mobile).
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (!runningRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onmessage = (e) => { try { handleEvent(JSON.parse(e.data)); } catch {} };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const resp = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(sess.model)}`, {
        method: "POST",
        body: offer.sdp,
        headers: { Authorization: `Bearer ${sess.value}`, "Content-Type": "application/sdp" },
      });
      if (!resp.ok) throw new Error("negotiate failed");
      await pc.setRemoteDescription({ type: "answer", sdp: await resp.text() });
      if (runningRef.current) setPhase("listening");
    } catch (e) {
      if (!runningRef.current) return;
      setErrorMsg(e?.name === "NotAllowedError" ? "Microphone access is needed for voice mode." : "Voice is unavailable right now. Please try again.");
      setPhase("error");
    }
  };

  const handleEvent = (ev) => {
    if (!runningRef.current || !ev?.type) return;
    switch (ev.type) {
      case "input_audio_buffer.speech_started": setPhase("listening"); break;
      case "input_audio_buffer.speech_stopped": setPhase("thinking"); break;
      case "response.created": replyRef.current = ""; setPhase("thinking"); break;
      case "output_audio_buffer.started": setPhase("speaking"); break;
      // Stream MAXx's reply into the caption as it speaks (caption is MAXx's words only — no speaker mix-ups).
      case "response.output_audio_transcript.delta":
        if (ev.delta) { replyRef.current += ev.delta; setCaption(replyRef.current.trim()); } break;
      case "response.output_audio_transcript.done":
        if (ev.transcript) setCaption(String(ev.transcript).trim()); break;
      case "output_audio_buffer.stopped":
      case "response.done": setPhase("listening"); break;
      default: break;
    }
  };

  const end = () => { cleanup(); onClose?.(null); };

  if (!open) return null;
  const ringColor = phase === "speaking" ? "rgb(34 197 94)" : phase === "thinking" ? "rgb(245 158 11)" : phase === "error" ? "rgb(239 68 68)" : "rgb(59 130 246)";
  return (
    <div data-testid="voice-session" className="fixed inset-0 z-[70] flex flex-col items-center justify-between bg-neutral-950 px-6 py-10 text-white">
      <audio ref={audioRef} autoPlay playsInline />
      <div className="flex w-full items-center justify-between">
        <span className="text-sm font-medium text-white/60">Live voice · {tab}</span>
        <button data-testid="voice-close" onClick={end} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"><X className="h-5 w-5" /></button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-8">
        <div
          data-testid="voice-orb"
          className="relative flex h-44 w-44 items-center justify-center rounded-full"
          style={{ background: `radial-gradient(circle at 50% 40%, ${ringColor}, rgba(0,0,0,0.2))` }}
        >
          <span className={cn("absolute inset-0 rounded-full", phase !== "error" && "animate-ping")} style={{ background: ringColor, opacity: 0.25 }} />
          <span className="absolute inset-0 rounded-full ring-1 ring-white/20" />
        </div>
        <div className="text-center">
          <div data-testid="voice-phase" className="text-lg font-semibold">{phase === "error" ? errorMsg : LABELS[phase]}</div>
          {phase !== "error" ? <p data-testid="voice-caption" className="mt-3 max-w-md text-sm leading-relaxed text-white/70">{caption || "Just start talking — I'm listening, and you can jump in any time."}</p> : null}
        </div>
      </div>

      <div className="flex w-full flex-col items-center gap-3">
        <p className="text-xs text-white/40">Talk naturally — MAXx replies out loud and you can interrupt any time.</p>
        <button data-testid="voice-end-btn" onClick={end} className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-neutral-900 hover:bg-white/90">End</button>
      </div>
    </div>
  );
}
